import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

export interface CN19ReportParams {
  formatType: string; // Required parameter
  designatedOperator?: string;
  year?: number;
  currency?: string;
  inbound?: {
    normalIbrs: {
      weight?: number;
      items?: number;
    };
    bulkyIbrs: {
      weight?: number;
      items?: number;
    };
  };
  outbound?: {
    normalIbrs: {
      weight?: number;
      items?: number;
    };
    bulkyIbrs: {
      weight?: number;
      items?: number;
    };
  };
}

export interface CN19ReportResponse {
  message?: string;
  success?: boolean;
  data?: any;
  content?: Blob;
  filename?: string;
}

export function useCN19Report() {
  return useMutation<CN19ReportResponse, Error, CN19ReportParams>({
    mutationFn: async (params: CN19ReportParams) => {
      // Create URLSearchParams for CN19 endpoint
      const searchParams = new URLSearchParams();
      
      // Add required formatType parameter
      searchParams.append('formatType', params.formatType);
      
      // Add optional parameters
      if (params.designatedOperator) {
        searchParams.append('designatedOperator', params.designatedOperator);
      }
      
      if (params.year) {
        searchParams.append('year', params.year.toString());
      }
      
      if (params.currency) {
        searchParams.append('currency', params.currency);
      }
      
      // Add inbound IBRS parameters
      if (params.inbound?.normalIbrs?.weight !== undefined) {
        searchParams.append('inbound.normalIbrs.weight', params.inbound.normalIbrs.weight.toString());
      }
      
      if (params.inbound?.normalIbrs?.items !== undefined) {
        searchParams.append('inbound.normalIbrs.items', params.inbound.normalIbrs.items.toString());
      }
      
      if (params.inbound?.bulkyIbrs?.weight !== undefined) {
        searchParams.append('inbound.bulkyIbrs.weight', params.inbound.bulkyIbrs.weight.toString());
      }
      
      if (params.inbound?.bulkyIbrs?.items !== undefined) {
        searchParams.append('inbound.bulkyIbrs.items', params.inbound.bulkyIbrs.items.toString());
      }
      
      // Add outbound IBRS parameters
      if (params.outbound?.normalIbrs?.weight !== undefined) {
        searchParams.append('outbound.normalIbrs.weight', params.outbound.normalIbrs.weight.toString());
      }
      
      if (params.outbound?.normalIbrs?.items !== undefined) {
        searchParams.append('outbound.normalIbrs.items', params.outbound.normalIbrs.items.toString());
      }
      
      if (params.outbound?.bulkyIbrs?.weight !== undefined) {
        searchParams.append('outbound.bulkyIbrs.weight', params.outbound.bulkyIbrs.weight.toString());
      }
      
      if (params.outbound?.bulkyIbrs?.items !== undefined) {
        searchParams.append('outbound.bulkyIbrs.items', params.outbound.bulkyIbrs.items.toString());
      }

      // Call the CN19 API endpoint with GET request
      const response = await axios.get(`/reports/cn19?${searchParams.toString()}`, {
        // HTML returns JSON with table data, PDF returns binary files
        responseType: params.formatType === 'html' ? 'json' : 'blob',
      });

      // For HTML format, return the table data
      if (params.formatType === 'html') {
        return {
          success: true,
          message: 'CN19 request processed successfully',
          data: response.data
        };
      }

      // For PDF/other formats, return the blob for file download
      let fileName: string | null = null;
      
      // Try to extract the filename from the Content-Disposition header
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }
      
      // If we couldn't get the filename from the header, generate one
      if (!fileName) {
        // Extract the current date in YYYYMMDD format for filename
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const timestamp = String(date.getHours()).padStart(2, '0') + 
                          String(date.getMinutes()).padStart(2, '0') + 
                          String(date.getSeconds()).padStart(2, '0');
        
        // Format filename with timestamp
        fileName = `CN19_Report_${year}${month}${day}_${timestamp}.${params.formatType}`;
      }
      
      const mimeType = params.formatType === 'pdf' 
        ? 'application/pdf' 
        : 'application/octet-stream';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { 
        success: true,
        message: `${params.formatType.toUpperCase()} downloaded successfully`,
        content: response.data,
        filename: fileName
      };
    },
  });
} 